import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  Box,
  Typography,
  useTheme,
  useMediaQuery
} from '@mui/material';
import type { OpportunityWithSnapshot } from '../../types/opportunity';
import { OpportunityRowSection } from './OpportunityRowSection';

interface OpportunityTableProps {
  opportunities: OpportunityWithSnapshot[];
  loading?: boolean;
  networkStatusStale?: boolean;
  networkStatusAvailable?: boolean;
}

// Number of skeleton rows to show while loading
const SKELETON_ROWS = 10;

// Korean exchanges (indexes: Upbit=0, Bithumb=1, Coinone=2)
const KOREAN_EXCHANGES = [0, 1, 2];

const isKoreanExchange = (exchangeIndex: number): boolean => {
  return KOREAN_EXCHANGES.includes(exchangeIndex);
};

interface SectionedOpportunities {
  toKorea: OpportunityWithSnapshot[];     // From global to Korea (역프)
  fromKorea: OpportunityWithSnapshot[];   // From Korea to global (김프)
  globalToGlobal: OpportunityWithSnapshot[]; // Global to global
}

export const OpportunityTable: React.FC<OpportunityTableProps> = ({ 
  opportunities, 
  loading = false,
  networkStatusStale = false,
  networkStatusAvailable = false
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  // Categorize opportunities into three sections
  const sectionedOpportunities = useMemo((): SectionedOpportunities => {
    const result: SectionedOpportunities = {
      toKorea: [],
      fromKorea: [],
      globalToGlobal: []
    };
    
    opportunities.forEach(opp => {
      const buyIsKorean = isKoreanExchange(opp.buy_exchange);
      const sellIsKorean = isKoreanExchange(opp.sell_exchange);
      
      if (sellIsKorean && !buyIsKorean) {
        // Selling TO Korea (from global) - 김프
        result.toKorea.push(opp);
      } else if (buyIsKorean && !sellIsKorean) {
        // Buying FROM Korea (to global) - 역프
        result.fromKorea.push(opp);
      } else {
        // Both global or both Korean
        result.globalToGlobal.push(opp);
      }
    });
    
    return result;
  }, [opportunities]);

  // Loading skeleton
  if (loading) {
    return (
      <TableContainer component={Paper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{t('table.headers.exchangePair')}</TableCell>
              <TableCell align="right">{t('table.headers.price')}</TableCell>
              <TableCell align="right">{t('table.headers.volume')}</TableCell>
              <TableCell align="right">{t('table.headers.profitTime')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from({ length: SKELETON_ROWS }).map((_, index) => (
              <React.Fragment key={index}>
                <TableRow>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Skeleton variant="circular" width={24} height={24} />
                      <Skeleton variant="text" width={100} />
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Skeleton variant="text" width={80} />
                  </TableCell>
                  <TableCell align="right">
                    <Skeleton variant="text" width={60} />
                  </TableCell>
                  <TableCell align="right">
                    <Skeleton variant="text" width={70} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Skeleton variant="circular" width={24} height={24} />
                      <Skeleton variant="text" width={100} />
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Skeleton variant="text" width={80} />
                  </TableCell>
                  <TableCell align="right">
                    <Skeleton variant="text" width={60} />
                  </TableCell>
                  <TableCell align="right">
                    <Skeleton variant="text" width={50} />
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  // Empty state
  if (opportunities.length === 0) {
    return (
      <TableContainer component={Paper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{t('table.headers.exchangePair')}</TableCell>
              <TableCell align="right">{t('table.headers.price')}</TableCell>
              <TableCell align="right">{t('table.headers.volume')}</TableCell>
              <TableCell align="right">{t('table.headers.profitTime')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                No arbitrage opportunities available
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  // Render a section with its opportunities
  const renderSection = (title: string, emoji: string, opportunities: OpportunityWithSnapshot[], isLast: boolean = false) => (
    <Box sx={{
      flex: isMobile ? '1 1 100%' : isTablet ? '1 1 50%' : '1',
      minWidth: 0,
      width: isMobile ? '100%' : isTablet ? '50%' : '33.333%',
      display: 'flex',
      flexDirection: 'column',
      minHeight: isMobile ? '400px' : '100%',
      maxWidth: isMobile ? '100%' : isTablet ? '50%' : '33.333%',
      overflowX: 'hidden',
      overflowY: 'visible',
      borderRight: isMobile ? 'none' : (isLast || (isTablet && !isMobile) ? 'none' : '2px solid'),
      borderBottom: isMobile ? '2px solid' : 'none',
      borderColor: 'divider',
      mb: isMobile ? 2 : 0,
      px: isMobile ? 0 : 1,
      '&:first-of-type': {
        pl: 0
      },
      '&:last-of-type': {
        pr: 0
      }
    }}>
      {/* Section Header */}
      <Box sx={{ 
        p: 1.5, 
        bgcolor: 'background.paper',
        borderBottom: '2px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        width: '100%'
      }}>
        <Typography variant="h6" sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
          {emoji}
        </Typography>
        <Typography variant="h6" sx={{ fontSize: { xs: '1rem', md: '1.2rem' }, fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography variant="caption" sx={{ ml: 'auto', color: 'text.secondary' }}>
          ({opportunities.length})
        </Typography>
      </Box>
      
      {/* Table Header */}
      <Box 
        sx={{ 
          display: 'flex', 
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          position: 'sticky',
          top: 0,
          zIndex: 1,
          width: '100%',
          py: 0.5,
          px: 1
        }}
      >
        <Box sx={{ width: '5%', fontSize: { xs: '0.8rem', md: '0.95rem' }, fontWeight: 600, textAlign: 'center' }}>
          ⭐
        </Box>
        <Box sx={{ width: '30%', fontSize: { xs: '0.85rem', md: '1rem' }, fontWeight: 600, minWidth: 0, pl: 1 }}>
          Pair
        </Box>
        <Box sx={{ width: '27.5%', fontSize: { xs: '0.85rem', md: '1rem' }, textAlign: 'right', fontWeight: 600, minWidth: 0, pr: 2 }}>
          Price
        </Box>
        <Box sx={{ width: '27.5%', fontSize: { xs: '0.85rem', md: '1rem' }, textAlign: 'left', fontWeight: 600, minWidth: 0, pl: 2 }}>
          Vol./Total
        </Box>
        <Box sx={{ width: '10%', fontSize: { xs: '0.85rem', md: '1rem' }, textAlign: 'right', fontWeight: 600, minWidth: 0, pr: 1 }}>
          %/Time
        </Box>
      </Box>
      
      {/* Opportunities List */}
      <Box sx={{
        flex: 1,
        overflowY: 'scroll',
        overflowX: 'visible',
        minHeight: '200px',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        pr: 0.5,
        scrollbarGutter: 'stable'
      }}>
        {opportunities.length === 0 ? (
          <Box sx={{ 
            p: 4, 
            textAlign: 'center',
            color: 'text.secondary'
          }}>
            <Typography variant="body2">
              No opportunities
            </Typography>
          </Box>
        ) : (
          opportunities.map((opportunity, index) => (
            <Box
              key={`${opportunity.buy_exchange}${opportunity.buy_quote}${opportunity.base}${opportunity.sell_quote}${opportunity.sell_exchange}`}
              sx={{
                mb: 0.5,
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}
            >
              <OpportunityRowSection
                opportunity={opportunity}
                index={index}
                networkStatusStale={networkStatusStale}
                networkStatusAvailable={networkStatusAvailable}
              />
            </Box>
          ))
        )}
      </Box>
    </Box>
  );

  // Main render with three sections
  return (
    <Box sx={{
      height: '100%',
      width: '100%',
      maxWidth: '100vw',
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      flexWrap: (isTablet && !isMobile) ? 'wrap' : 'nowrap',
      gap: 0,
      boxSizing: 'border-box',
      overflow: 'visible',  // Allow tooltips to display outside container
      px: isMobile ? 0 : { md: 2, lg: 3 }  // Add padding for 2+ columns
    }}>
      {/* TO Korea (김프) - Selling to Korean exchanges */}
      {renderSection('김프', '🇰🇷', sectionedOpportunities.toKorea, false)}
      
      {/* FROM Korea (역프) - Buying from Korean exchanges */}
      {renderSection('역프', '🌎', sectionedOpportunities.fromKorea, false)}
      
      {/* Global to Global */}
      {renderSection('', '🌐', sectionedOpportunities.globalToGlobal, true)}
    </Box>
  );
};

// Export with memo for performance
export default React.memo(OpportunityTable);